// Book a visit in Blueprint (BookPage.tsx): the week of arrival windows as ui-web's WindowWeek draws it,
// in ready-to-show words, from book-model's week (weekGrid) and the soonest that fits (soonestFit).
import type { WindowWeekDay, WindowWeekProps, WindowWeekRow } from '@flowology/ui-web/fl';
import {
  dayLabel,
  hoursWords,
  WEEK_PAGES,
  weekRangeWords,
  windowLabel,
  type WeekCell,
  type WeekGrid,
} from '../book-model.ts';

/** "8–10" over "a.m.": a window's row heading, narrow enough for a phone. */
export function windowHead([s, e]: readonly [number, number]): { range: string; part: string } {
  const label = windowLabel([s, e]);
  const cut = label.lastIndexOf(' ');
  return { range: label.slice(0, cut), part: label.slice(cut + 1) };
}

/** What the week's grid says: its days, its windows and cells, the chosen one marked. */
export function weekWords(
  grid: WeekGrid | undefined,
  today: string,
  chosen: { date: string; window: readonly [number, number] | null },
): { range: string; days: WindowWeekDay[] | null; rows: WindowWeekRow[] } {
  if (!grid) return { range: '', days: null, rows: [] };
  return {
    range: weekRangeWords(grid.from, grid.to),
    days: grid.days.map((x) => ({
      label: dayLabel(x.date, today),
      day: x.today ? 'Today' : x.day,
      ...(x.today ? { short: x.short } : {}),
      num: x.num,
      ...(x.today ? { today: true } : {}),
      ...(x.date === chosen.date ? { chosen: true } : {}),
    })),
    rows: grid.rows.map((r) => ({
      label: r.label,
      ...windowHead(r.window),
      cells: r.cells.map((c) => ({
        text: c.text,
        name: c.name,
        state: c.state,
        ...(chosen.date === c.date && chosen.window?.[0] === c.window[0] ? { chosen: true } : {}),
      })),
    })),
  };
}

/** The soonest that fits, said over the week: with what picks it, or why there's none. */
export function soonestWords(
  soonest: { found: WeekCell } | { need: number } | { none: true } | { unknown: true },
  length: number | null,
  today: string,
): WindowWeekProps['soonest'] {
  const forLength = length !== null ? ` ${hoursWords(length)}` : '';
  if ('found' in soonest)
    return {
      words: `${length !== null ? `Soonest that fits${forLength}` : 'Soonest open'}:`,
      pick: `${dayLabel(soonest.found.date, today)}, ${windowLabel(soonest.found.window)}`,
    };
  if ('none' in soonest)
    return {
      words: `${length !== null ? `Nothing fits${forLength}` : 'Nothing is open'} in the next ${WEEK_PAGES} weeks.`,
    };
  if ('unknown' in soonest) return { words: 'Couldn’t find the soonest window. Pick one below.' };
  return { words: 'Finding the soonest window that fits…' };
}

/** What the marks mean, under the week. */
export const weekNote = (length: number | null): string =>
  'Hours of technician time open in each arrival window.' +
  (length !== null ? ` Muted: there’s room, but nobody can fit ${hoursWords(length)} starting in it.` : '') +
  ' Hatched: full. Blank: already over. A full or closed window, or the one under way, can still be booked.';
